import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Mail,
  RefreshCcw,
  Link as LinkIcon,
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
  ShieldCheck,
  Sparkles,
  FileText,
  Search,
  BarChart3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import ScoreGauge from "@/components/ScoreGauge";
import HeatmapTable from "@/components/HeatmapTable";
import { Progress } from "@/components/ui/progress";
import PublicLayout from "@/components/layout/PublicLayout";

const STEPS = [
  { label: "Document verwerken", icon: FileText, dur: 4500 },
  { label: "Lijnposten extraheren", icon: Search, dur: 8000 },
  { label: "Marktprijzen vergelijken", icon: BarChart3, dur: 16000 },
  { label: "Onderhandelingstips genereren", icon: Sparkles, dur: 22000 },
];

function LoadingState({ pct }) {
  const idx = pct < 25 ? 0 : pct < 55 ? 1 : pct < 85 ? 2 : 3;
  return (
    <div className="max-w-xl mx-auto bg-white rounded-oc border border-oc-border p-10 text-center">
      <div className="w-16 h-16 rounded-full bg-oc-subtle flex items-center justify-center mx-auto">
        <Sparkles className="w-7 h-7 text-oc-secondary animate-pulse" />
      </div>
      <h2 className="font-display text-oc-primary text-[26px] font-bold mt-6">Analyse loopt…</h2>
      <p className="text-oc-textSecondary mt-2 text-[15px]">Dit duurt doorgaans 30 tot 60 seconden.</p>
      <div className="mt-8 space-y-3 text-left">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = i < idx;
          const active = i === idx;
          return (
            <div
              key={i}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-oc-btn transition-colors ${
                active ? "bg-oc-subtle" : ""
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  done ? "bg-oc-fair text-white" : active ? "bg-oc-primary text-white" : "bg-oc-subtle text-oc-textSecondary"
                }`}
              >
                {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={`text-[14px] font-medium ${done ? "text-oc-textSecondary line-through" : "text-oc-primary"}`}>
                {s.label}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-8">
        <Progress value={pct} className="h-2 bg-oc-subtle" />
        <p className="mt-2 text-oc-textSecondary text-[12px] font-mono">{pct}%</p>
      </div>
    </div>
  );
}

function StatusCard({ title, value, hint, color = "text-oc-primary", testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-oc border border-oc-border p-7">
      <p className="text-[12px] uppercase tracking-[0.08em] font-semibold text-oc-textSecondary">{title}</p>
      <p className={`mt-3 font-mono font-semibold text-[28px] ${color}`}>{value}</p>
      {hint && <p className="text-oc-textSecondary text-[13px] mt-1">{hint}</p>}
    </div>
  );
}

const RISK_STYLES = {
  Kritiek: { bar: "bg-oc-excessive", chip: "bg-oc-excessive text-white", border: "border-oc-excessive/30" },
  Hoog: { bar: "bg-oc-high", chip: "bg-oc-high text-white", border: "border-oc-high/30" },
  Aandacht: { bar: "bg-yellow-500", chip: "bg-yellow-500 text-white", border: "border-yellow-500/30" },
  Info: { bar: "bg-oc-secondary", chip: "bg-oc-secondary text-white", border: "border-oc-secondary/30" },
  "Oké": { bar: "bg-oc-fair", chip: "bg-oc-fair text-white", border: "border-oc-fair/30" },
};

function FindingCard({ f }) {
  const style = RISK_STYLES[f.risk_level] || RISK_STYLES.Info;
  return (
    <div data-testid={`finding-${f.risk_level || "info"}`} className={`relative bg-white rounded-oc border ${style.border} p-6 overflow-hidden`}>
      <span className={`absolute top-0 left-0 h-1 w-full ${style.bar}`} />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h3 className="font-display font-semibold text-oc-primary text-[16px] leading-snug pr-2">{f.title}</h3>
        <div className="flex gap-2 shrink-0">
          <span className={`text-[11px] uppercase tracking-[0.06em] font-bold px-2.5 py-1 rounded-full ${style.chip}`}>
            {f.risk_level}
          </span>
          {f.confidence && (
            <span className="text-[11px] uppercase tracking-[0.06em] font-semibold px-2.5 py-1 rounded-full bg-oc-subtle text-oc-textSecondary border border-oc-border">
              {f.confidence}
            </span>
          )}
        </div>
      </div>
      {f.analysis && <p className="text-oc-textPrimary text-[14px] mt-3 leading-relaxed">{f.analysis}</p>}
      {f.practical_impact && (
        <p className="text-oc-textSecondary text-[13px] mt-2 leading-relaxed">
          <strong className="text-oc-primary">Praktische impact:</strong> {f.practical_impact}
        </p>
      )}
      {f.what_to_do && (
        <div className="mt-3 flex items-start gap-2 text-[13px] text-oc-textPrimary p-3 rounded-oc-btn bg-oc-subtle border border-oc-border">
          <Lightbulb className="w-4 h-4 text-oc-secondary mt-0.5 shrink-0" />
          <span>{f.what_to_do}</span>
        </div>
      )}
      {f.market_reference && (
        <p className="text-oc-textSecondary text-[12px] mt-3 italic">
          Referentie: {f.market_reference}
        </p>
      )}
    </div>
  );
}

export default function Rapport() {
  const { orderId } = useParams();
  const [pct, setPct] = useState(8);
  const [record, setRecord] = useState(null);
  const [error, setError] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [emailTarget, setEmailTarget] = useState("");

  useEffect(() => {
    let cancelled = false;
    let timer;
    const tick = async () => {
      try {
        const status = await api.get(`/analyses/${orderId}/status`);
        if (cancelled) return;
        if (status.data.analysis_status === "completed") {
          const rec = await api.get(`/analyses/${orderId}`);
          setRecord(rec.data);
          setPct(100);
          return;
        }
        if (status.data.analysis_status === "failed") {
          setError("De analyse is mislukt. Neem contact op met info@offerteexpert.be");
          return;
        }
        setPct((p) => Math.min(94, p + 6));
        timer = setTimeout(tick, 3000);
      } catch (e) {
        if (e?.response?.status === 410) {
          setError("Dit rapport is verlopen en verwijderd conform ons privacybeleid.");
          return;
        }
        if (e?.response?.status === 404) {
          setError("Rapport niet gevonden.");
          return;
        }
        timer = setTimeout(tick, 4000);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [orderId]);

  const handleDownload = () => {
    const url = `${API}/analyses/${orderId}/pdf`;
    window.open(url, "_blank");
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link gekopieerd naar je klembord.");
    } catch {
      toast.error("Kon link niet kopiëren.");
    }
  };
  const handleEmail = async () => {
    if (!emailTarget) return;
    setEmailing(true);
    try {
      await api.post(`/analyses/${orderId}/email-report`, { email: emailTarget });
      toast.success("Rapport verstuurd naar " + emailTarget);
    } catch {
      toast.error("Versturen mislukt.");
    } finally {
      setEmailing(false);
    }
  };

  if (error) {
    return (
      <PublicLayout>
        <div className="min-h-[60vh] flex items-center justify-center p-6">
          <div className="bg-white border border-oc-border rounded-oc max-w-lg p-10 text-center">
            <AlertTriangle className="w-10 h-10 text-oc-high mx-auto" />
            <h2 className="font-display text-[22px] font-bold mt-4 text-oc-primary">Rapport niet beschikbaar</h2>
            <p className="text-oc-textSecondary mt-2">{error}</p>
            <Link to="/analyse">
              <Button className="mt-6 bg-oc-primary hover:bg-oc-secondary text-white rounded-oc-btn">Nieuwe analyse starten</Button>
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!record) {
    return (
      <PublicLayout>
        <div className="py-12 px-6">
          <Link to="/" className="oc-container flex items-center gap-2 text-oc-textSecondary hover:text-oc-primary mb-10 text-[14px]">
            <ArrowLeft className="w-4 h-4" /> Terug naar home
          </Link>
          <LoadingState pct={pct} />
        </div>
      </PublicLayout>
    );
  }

  const a = record.analysis || {};
  const totaal = a.totaal_geoffreerd || 0;
  const minM = a.totaal_marktprijs_min || 0;
  const maxM = a.totaal_marktprijs_max || 0;
  const verschil = totaal - (minM + maxM) / 2;

  return (
    <PublicLayout>
    <div className="bg-oc-bg pb-20" data-testid="rapport-container">
      <div className="oc-hero-gradient text-white">
        <div className="oc-container py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-white/75 hover:text-white text-[14px]">
            <ArrowLeft className="w-4 h-4" /> Terug naar home
          </Link>
          <div className="mt-6 flex flex-wrap items-end gap-6 justify-between">
            <div>
              <span className="inline-block text-[12px] uppercase tracking-[0.1em] font-semibold text-white/65">
                OfferteExpert Rapport
              </span>
              <h1 className="font-display font-bold text-[36px] sm:text-[44px] mt-2 leading-tight">
                {record.offerte_type} · {record.regio}
              </h1>
              <p className="text-white/70 mt-2 font-mono text-[13px]">Referentie {record.id}</p>
            </div>
            <div className="text-white/75 text-[13px]">
              <p>Aangemaakt: {new Date(record.created_at).toLocaleString("nl-BE")}</p>
              <p>Beschikbaar tot: {new Date(record.expires_at).toLocaleDateString("nl-BE")}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="oc-container -mt-10">
        <div className="bg-white rounded-oc border border-oc-border p-8 lg:p-12 grid lg:grid-cols-3 gap-10 items-center shadow-oc-card">
          <ScoreGauge score={a.score || 0} label={a.score_label || ""} />
          <div className="lg:col-span-2 space-y-4">
            <h2 className="font-display font-bold text-oc-primary text-[26px]">Samenvatting</h2>
            <p className="text-oc-textPrimary leading-[1.7]">{a.samenvatting}</p>
            {a.btw_opmerking && (
              <div className="rounded-oc-btn bg-oc-subtle border border-oc-border p-4 flex gap-3 text-[14px] text-oc-textPrimary">
                <ShieldCheck className="w-4 h-4 text-oc-secondary mt-0.5 shrink-0" />
                <p>{a.btw_opmerking}</p>
              </div>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          <StatusCard
            testId="card-uw-offerte"
            title="Uw offerte"
            value={`€ ${totaal.toLocaleString("nl-BE", { minimumFractionDigits: 2 })}`}
            hint="Totaalbedrag offerte"
          />
          <StatusCard
            testId="card-marktprijs"
            title="Marktprijs"
            value={`€ ${minM.toLocaleString("nl-BE", { minimumFractionDigits: 0 })} – ${maxM.toLocaleString("nl-BE", { minimumFractionDigits: 0 })}`}
            hint="Min / max bereik"
          />
          <StatusCard
            testId="card-besparing"
            title="Mogelijke besparing"
            value={verschil > 0 ? `€ ${verschil.toLocaleString("nl-BE", { minimumFractionDigits: 0 })}` : "Geen"}
            hint={verschil > 0 ? "ten opzichte van marktgemiddelde" : "uw prijs zit in lijn"}
            color={verschil > 0 ? "text-oc-fair" : "text-oc-textPrimary"}
          />
        </div>

        <div className="mt-10">
          <h2 className="font-display font-bold text-oc-primary text-[26px] mb-5">Lijnposten</h2>
          <HeatmapTable items={a.lijnitems || []} />
        </div>

        {/* Risk findings — only shown if the analysis includes them */}
        {Array.isArray(a.findings) && a.findings.length > 0 && (
          <div className="mt-10" data-testid="findings-section">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
              <h2 className="font-display font-bold text-oc-primary text-[26px]">Aandachtspunten &amp; risico's</h2>
              {a.overall_confidence && (
                <span className="text-[12px] uppercase tracking-[0.08em] font-semibold text-oc-textSecondary">
                  Vertrouwen analyse: <span className="text-oc-primary">{a.overall_confidence}</span>
                </span>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {a.findings.map((f, i) => <FindingCard key={i} f={f} />)}
            </div>
          </div>
        )}

        <div className="mt-10 grid lg:grid-cols-3 gap-5">
          <div data-testid="positives-section" className="bg-[rgba(52,199,89,0.06)] border border-[rgba(52,199,89,0.25)] rounded-oc p-7">
            <h3 className="font-display font-semibold text-oc-fair text-[18px] flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" /> Wat goed is
            </h3>
            <ul className="mt-4 space-y-2 text-oc-textPrimary text-[15px] list-disc pl-5">
              {(a.positieve_punten || []).map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
          {a.waarschuwing && (
            <div data-testid="warning-section" className="bg-[rgba(234,88,12,0.06)] border border-[rgba(234,88,12,0.25)] rounded-oc p-7">
              <h3 className="font-display font-semibold text-oc-high text-[18px] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> Let op
              </h3>
              <p className="mt-4 text-oc-textPrimary text-[15px] leading-relaxed">{a.waarschuwing}</p>
            </div>
          )}
          <div data-testid="tips-section" className={`bg-[rgba(27,75,138,0.06)] border border-[rgba(27,75,138,0.2)] rounded-oc p-7 ${a.waarschuwing ? "" : "lg:col-span-2"}`}>
            <h3 className="font-display font-semibold text-oc-secondary text-[18px] flex items-center gap-2">
              <Lightbulb className="w-5 h-5" /> Onderhandelingstips
            </h3>
            <ol className="mt-4 space-y-3 text-oc-textPrimary text-[15px] list-decimal pl-5">
              {(a.onderhandelingstips || []).map((t, i) => <li key={i} className="leading-relaxed">{t}</li>)}
            </ol>
          </div>
        </div>

        <div className="mt-12 bg-white border border-oc-border rounded-oc p-8" data-testid="actions-section">
          <h3 className="font-display font-semibold text-oc-primary text-[20px]">Acties</h3>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Button
              data-testid="action-download"
              onClick={handleDownload}
              className="bg-oc-primary hover:bg-oc-secondary text-white h-12 rounded-oc-btn font-semibold flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" /> Download PDF
            </Button>
            <Button
              data-testid="action-copy"
              onClick={handleCopyLink}
              variant="outline"
              className="h-12 rounded-oc-btn border-oc-border text-oc-primary hover:bg-oc-subtle font-semibold flex items-center justify-center gap-2"
            >
              <LinkIcon className="w-4 h-4" /> Kopieer link
            </Button>
            <Link to="/analyse" className="contents">
              <Button
                data-testid="action-new"
                variant="outline"
                className="h-12 rounded-oc-btn border-oc-border text-oc-primary hover:bg-oc-subtle font-semibold flex items-center justify-center gap-2 w-full"
              >
                <RefreshCcw className="w-4 h-4" /> Nieuwe analyse
              </Button>
            </Link>
            <div className="flex gap-2 sm:col-span-2 lg:col-span-1">
              <Input
                data-testid="email-target-input"
                placeholder="E-mailadres"
                value={emailTarget}
                onChange={(e) => setEmailTarget(e.target.value)}
                className="h-12 rounded-oc-btn"
              />
              <Button
                data-testid="action-email"
                onClick={handleEmail}
                disabled={emailing || !emailTarget}
                className="bg-oc-secondary hover:bg-oc-primary text-white h-12 rounded-oc-btn font-semibold"
              >
                <Mail className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <p className="mt-10 text-center text-oc-textSecondary text-[13px] max-w-3xl mx-auto leading-relaxed">
          Dit rapport is opgesteld op basis van actuele markttarieven en regionale prijsdata en dient enkel als indicatie. OfferteExpert (Webtron BV) is niet aansprakelijk voor beslissingen op basis van dit rapport. Raadpleeg bij twijfel een erkend expert.
        </p>
      </div>
    </div>
    </PublicLayout>
  );
}
